
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { Assessment } from "../types";

const PAGE_SIZE = 20

interface AssessmentPage {
  assessments: Assessment[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export const useAssessments = (
  isProvider: boolean | undefined, 
  authChecked: boolean, 
  page: number = 0,
  status?: "draft" | "completed" | "prescribed" | "denied"
) => {
  return useQuery<AssessmentPage>({
    queryKey: ["provider-assessments", page, status],
    queryFn: async () => {
      console.log('Fetching assessments page:', page, 'status:', status)
      
      const from = page * PAGE_SIZE
      const to = from + PAGE_SIZE - 1

      let query = supabase
        .from("assessments")
        .select(`
          *,
          profiles (
            id,
            first_name,
            last_name,
            email,
            is_provider,
            utm_source,
            utm_medium,
            utm_campaign,
            utm_term,
            utm_content,
            created_at,
            updated_at
          )
        `, { count: 'exact' })

      // Filter by status if provided
      if (status) {
        query = query.eq('status', status)
      }

      // Add completed_at filter only for non-draft assessments
      if (status && status !== 'draft') {
        query = query.not('completed_at', 'is', null)
      }

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to)

      if (error) {
        console.error('Error fetching assessments:', error)
        throw error
      }

      const totalCount = count || 0
      const totalPages = Math.ceil(totalCount / PAGE_SIZE)

      return {
        assessments: data as Assessment[],
        totalCount,
        currentPage: page,
        totalPages,
        hasNextPage: page < totalPages - 1,
        hasPreviousPage: page > 0
      }
    },
    enabled: authChecked && isProvider,
  })
}
